import { useCallback, useEffect, useMemo, useState } from 'react'
import Attachments from '../components/Attachments.jsx'
import Sheet from '../components/Sheet.jsx'
import Links from '../components/Links.jsx'
import {
  CADENCES, cadenceLabel, getDefaults, markAsSubscription, monthsPaying, nextRenewal,
  paidSoFar, perMonth, perYear, removeSubscription, saveSubscription, setUnused, sortSubs, totals,
} from '../lib/subs.js'
import { addReminder } from '../lib/notes.js'
import { parseAmount, peso, shortDate, todayISO } from '../lib/format.js'

function daysOut(iso) {
  const a = new Date(todayISO() + 'T00:00:00')
  const b = new Date(String(iso).slice(0, 10) + 'T00:00:00')
  return Math.round((b - a) / 86400000)
}

/** "in 4d" when it is close enough to matter, a plain date when it is not. */
function renewLabel(date) {
  if (!date) return { text: 'no renewal day set', cls: 'faint' }
  const iso = todayISO(date)
  const d = daysOut(iso)
  if (d <= 0) return { text: 'renews today', cls: 'late' }
  if (d <= 7) return { text: 'in ' + d + 'd', cls: 'soon' }
  return { text: shortDate(iso), cls: '' }
}

/**
 * Subscriptions.
 *
 * The list is the small part. The reason this screen exists is the two numbers
 * at the top — what it costs a month, and what that is over a year. The yearly
 * figure is the one that changes behaviour, so it is the one set in the display
 * face. Under it, what each one has cost since he started paying it.
 *
 * Nothing here judges. It does not say a subscription is too expensive or that
 * he has too many. It shows what he is paying and leaves the decision alone.
 */
export default function Subscriptions({ toast }) {
  const [defaults, setDefaults] = useState(null)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setDefaults(await getDefaults())
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  const subs = useMemo(
    () => sortSubs((defaults ?? []).filter((d) => d.is_subscription)),
    [defaults],
  )
  const others = useMemo(
    () => (defaults ?? []).filter((d) => !d.is_subscription),
    [defaults],
  )
  const t = useMemo(() => totals(subs), [subs])

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your subscriptions. {error}</p>
      </div>
    )
  }
  if (!defaults) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  return (
    <>
      {subs.length ? (
        <div className="card">
          <div className="pad">
            <div className="in-total">
              <span className="num serif">{peso(t.perYear)}</span>
              <span className="lbl">a year, at today&rsquo;s prices</span>
            </div>
            <div className="subgrid">
              <div>
                <span className="lbl">A month</span>
                <b className="num">{peso(t.perMonth)}</b>
              </div>
              <div>
                <span className="lbl">This month</span>
                <b className="num">{peso(t.thisMonth)}</b>
              </div>
              <div>
                <span className="lbl">Running</span>
                <b className="num">{t.liveCount}</b>
              </div>
            </div>

            {t.idleCount ? (
              <p className="lbl idle-note">
                {peso(t.idlePerYear)} a year of that is in {t.idleCount}{' '}
                {t.idleCount === 1 ? 'thing' : 'things'} you marked unused.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="empty">
          <h2 className="serif">No subscriptions yet</h2>
          <p>
            The things that bill you forever. They load into each payout like any other
            monthly item, and this screen is where you see what they add up to.
          </p>
        </div>
      )}

      {subs.map((s) => {
        const next = nextRenewal(s)
        const r = renewLabel(next)
        const months = monthsPaying(s)
        const spent = paidSoFar(s)
        const cancel = s.cancel_by ? daysOut(s.cancel_by) : null
        return (
          <div className={'card sub' + (s.unused ? ' idle' : '')} key={s.id}>
            <div className="pad">
              <div className="plan-top">
                <div>
                  <h2 className="serif">{s.name}</h2>
                  <span className="lbl">
                    {cadenceLabel(s.every_n_months)}
                    {s.every_n_months > 1 ? ' · ' + peso(perMonth(s)) + ' a month' : ''}
                  </span>
                </div>
                <span className="amt num">{peso(s.amount)}</span>
              </div>

              <div className="plan-nums">
                <em className={'due ' + r.cls}>{r.text}</em>
                {months != null ? (
                  <span className="lbl">
                    {months} {months === 1 ? 'month' : 'months'} in
                    {spent != null ? ' · ' + peso(spent) + ' so far' : ''}
                  </span>
                ) : (
                  <span className="lbl">{peso(perYear(s))} a year</span>
                )}
              </div>

              {s.unused ? <p className="lbl idle-tag">You marked this unused</p> : null}

              {cancel != null ? (
                <p className={'lbl cancel-tag' + (cancel <= 14 ? ' hot' : '')}>
                  Cancel by {shortDate(s.cancel_by)}
                  {cancel >= 0 ? ' · ' + cancel + 'd' : ' · passed'}
                </p>
              ) : null}

              {s.note ? <p className="sub-note">{s.note}</p> : null}

              <Links target={{ type: 'subscription', id: s.id }} toast={toast} compact />
            </div>

            <div className="build-acts">
              <button type="button" onClick={() => setSheet({ kind: 'edit', sub: s })}>
                Edit
              </button>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await setUnused(s.id, !s.unused)
                    load()
                  } catch (e) {
                    toast(e.message || 'Could not update that.')
                  }
                }}
              >
                {s.unused ? 'Still using it' : 'Mark unused'}
              </button>
            </div>
          </div>
        )
      })}

      <div className="sheet-actions" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={() => setSheet({ kind: 'edit' })}>
          Add a subscription
        </button>
        {others.length ? (
          <button type="button" className="btn ghost" onClick={() => setSheet({ kind: 'pick' })}>
            Existing items
          </button>
        ) : null}
      </div>

      {sheet?.kind === 'edit' ? (
        <SubSheet
          sub={sheet.sub}
          count={subs.length}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          onRemove={(s) => setSheet({ kind: 'remove', sub: s })}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'pick' ? (
        <Sheet title="Which of these bills forever?" onClose={() => setSheet(null)}>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: 'var(--ink-dim)' }}>
            Lines from your payouts. Marking one a subscription puts it on this screen and
            lets it bill on its own schedule — useful for anything that is not monthly.
            The amount and where it sits in the payout do not change.
          </p>
          <div className="rows">
            {others.map((d) => (
              <button className="row" key={d.id} type="button"
                onClick={async () => {
                  try {
                    await markAsSubscription(d.id, true)
                    setSheet(null)
                    load()
                    toast(d.name + ' added')
                  } catch (e) {
                    toast(e.message || 'Could not update that.')
                  }
                }}>
                <div className="nm"><b style={{ fontWeight: 400 }}>{d.name}</b></div>
                <span className="amt num">{peso(d.amount)}</span>
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}

      {sheet?.kind === 'remove' ? (
        <Sheet title={'Remove ' + sheet.sub.name + '?'} onClose={() => setSheet(null)}>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--ink-dim)' }}>
            It stops loading into new payouts. Everything you have already paid stays in the
            record, because that is what actually happened.
          </p>
          <div className="sheet-actions">
            <button type="button" className="btn ghost" onClick={() => setSheet(null)}>
              Keep it
            </button>
            <button type="button" className="btn" onClick={async () => {
              try {
                await removeSubscription(sheet.sub.id, sheet.sub.name)
                setSheet(null)
                load()
                toast('Removed')
              } catch (e) {
                toast(e.message || 'Could not remove that.')
              }
            }}>Remove</button>
          </div>
        </Sheet>
      ) : null}
    </>
  )
}

/**
 * One renewal date rather than a day field and a month field. The cadence and
 * the date together say everything: a yearly plan renewing 14 March bills in
 * March, on the 14th, and nowhere else.
 */
function SubSheet({ sub, count, onClose, onSaved, onRemove, toast }) {
  const existingRenewal = useMemo(() => {
    if (!sub?.renews_on) return ''
    const now = new Date()
    const month = sub.anchor_month ? Number(sub.anchor_month) - 1 : now.getMonth()
    const last = new Date(now.getFullYear(), month + 1, 0).getDate()
    const day = Math.min(Number(sub.renews_on), last)
    return todayISO(new Date(now.getFullYear(), month, day))
  }, [sub])

  const [f, setF] = useState({
    name: sub?.name ?? '',
    amount: sub ? String(sub.amount ?? '') : '',
    every: String(sub?.every_n_months ?? 1),
    renews: existingRenewal,
    started: sub?.started_on ?? '',
    cancelBy: sub?.cancel_by ?? '',
    note: sub?.note ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [remind, setRemind] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))

  const amount = parseAmount(f.amount)
  const every = Number(f.every) || 1
  const valid = f.name.trim().length > 0 && amount > 0
  const yearly = (amount / every) * 12

  return (
    <Sheet title={sub ? 'Edit ' + sub.name : 'Add a subscription'} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !valid) return
          setBusy(true)
          try {
            const saved = await saveSubscription({
              id: sub?.id,
              name: f.name,
              amount,
              everyNMonths: every,
              renewsOn: f.renews,
              startedOn: f.started,
              cancelBy: f.cancelBy,
              note: f.note,
              sortOrder: sub?.sort_order ?? count,
            })
            /* Offered, never automatic — a reminder is his to ask for. */
            if (remind && f.cancelBy) {
              await addReminder({
                body: 'Cancel ' + saved.name,
                remindAt: new Date(f.cancelBy + 'T09:00:00').toISOString(),
                targetType: 'subscription',
                targetId: saved.id,
              })
            }
            onSaved()
          } catch (err) {
            toast?.(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="s-name">Name</label>
          <input id="s-name" required value={f.name} placeholder="Netflix"
            onChange={(e) => set('name', e.target.value)} />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-amt">Amount charged</label>
          <input id="s-amt" inputMode="decimal" required placeholder="0"
            value={f.amount} onChange={(e) => set('amount', e.target.value)} />
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-every">How often</label>
          <select id="s-every" value={f.every} onChange={(e) => set('every', e.target.value)}>
            {CADENCES.map((c) => (
              <option key={c.months} value={c.months}>{c.label}</option>
            ))}
          </select>
          {valid ? (
            <p className="hint">
              {peso(yearly)} a year
              {every > 1 ? ' · ' + peso(amount / every) + ' a month' : ''}
            </p>
          ) : null}
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-renews">Next renewal</label>
          <input id="s-renews" type="date" value={f.renews}
            onChange={(e) => set('renews', e.target.value)} />
          <p className="hint">
            {every > 1
              ? 'The month and the day both come from this, so it only loads into that month.'
              : 'The day it bills each month.'}
          </p>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-started">Paying since</label>
          <input id="s-started" type="date" max={todayISO()} value={f.started}
            onChange={(e) => set('started', e.target.value)} />
          <p className="hint">
            Optional. It is what gives you the total spent so far.
          </p>
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-cancel">Cancel by</label>
          <input id="s-cancel" type="date" value={f.cancelBy}
            onChange={(e) => set('cancelBy', e.target.value)} />
          {f.cancelBy ? (
            <label className="checkline">
              <input type="checkbox" checked={remind}
                onChange={(e) => setRemind(e.target.checked)} />
              <span>Remind me that morning</span>
            </label>
          ) : null}
        </div>

        <div className="field">
          <label className="lbl" htmlFor="s-note">Note</label>
          <input id="s-note" placeholder="Optional" value={f.note}
            onChange={(e) => set('note', e.target.value)} />
        </div>

        {/* The renewal receipt or the cancellation confirmation, kept on the
            subscription it belongs to. */}
        {sub?.id ? (
          <Attachments target={{ type: 'subscription', id: sub.id }} toast={toast} compact />
        ) : null}

        <button className="btn" type="submit" data-sheet-primary={!sub || undefined} disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Save'}
        </button>

        {sub ? (
          <button type="button" className="btn ghost" style={{ marginTop: 10 }}
            onClick={() => onRemove(sub)}>
            Remove this subscription
          </button>
        ) : null}
      </form>
    </Sheet>
  )
}
